import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { get, post } from "@/lib/api";
import { StatePill } from "@/components/StatePill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Send, ShieldCheck, Ban, ExternalLink, Check, Copy, Eye, EyeOff,
  LifeBuoy, Undo2, PauseCircle, PlayCircle, ClipboardList,
} from "lucide-react";

export function PeopleOnboarding({ isAdmin, onChanged }) {
  const [config, setConfig] = useState(null);
  const [invites, setInvites] = useState([]);
  const [acks, setAcks] = useState([]);

  const loadI = () => get("/core/invites").then((d) => { setInvites(d.invites); onChanged?.(); });
  useEffect(() => { get("/core/config").then(setConfig); loadI(); get("/desk/acknowledgements").then((d) => setAcks(d.groups)); }, []);

  return (
    <div className="space-y-8" data-testid="people-onboarding">
      {isAdmin && <InviteForm config={config} onCreated={loadI} />}

      <section>
        <h3 className="mb-3 flex items-center gap-2 font-display text-lg font-semibold text-mill-ink"><ClipboardList className="h-5 w-5 text-mill-moss" /> Onboarding status</h3>
        <div className="space-y-3">
          {invites.length === 0 && <p className="text-sm text-mill-muted">No invitations yet.</p>}
          {invites.map((inv) => <InviteRow key={inv.id} inv={inv} isAdmin={isAdmin} onChange={loadI} acks={acks} />)}
        </div>
      </section>

      {acks.length > 0 && (
        <section className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="ack-panel">
          <h3 className="font-display text-base font-semibold text-mill-ink">Policy acknowledgements</h3>
          {acks.map((g) => (
            <div key={g.policy_id} className="mt-3 text-sm">
              <p className="font-medium text-mill-ink">{g.policy_title} <span className="text-xs text-mill-muted">{g.version}</span></p>
              <p className="mt-1 text-xs text-[#065F46]">Acknowledged: {g.acknowledged.join(", ") || "none"}</p>
              {g.outstanding.length > 0 && <p className="mt-0.5 text-xs text-[#B45309]">Outstanding: {g.outstanding.join(", ")}</p>}
            </div>
          ))}
        </section>
      )}
    </div>
  );
}

function InviteForm({ config, onCreated }) {
  const [f, setF] = useState({ role: "volunteer", activation_rule: "approval", team: "", display_name: "", delivery_route: "", expiry_date: "", offered_optional_consents: [] });
  const [busy, setBusy] = useState(false);
  const [lastUrl, setLastUrl] = useState(null);
  const toggleOpt = (id) => setF((p) => ({ ...p, offered_optional_consents: p.offered_optional_consents.includes(id) ? p.offered_optional_consents.filter((x) => x !== id) : [...p.offered_optional_consents, id] }));
  const submit = async () => {
    if (!f.display_name || !f.delivery_route || !f.expiry_date) { toast.error("Complete name, delivery route and expiry."); return; }
    setBusy(true);
    try {
      const pw = await post("/core/pathways", { role: f.role, activation_rule: f.activation_rule, permitted_services: ["request_room"], offered_optional_consents: f.offered_optional_consents });
      const inv = await post("/core/invites", { pathway_id: pw.id, display_name: f.display_name, delivery_route: f.delivery_route, expiry_date: f.expiry_date, team: f.team });
      setLastUrl(inv.invite_url); onCreated();
      setF({ ...f, display_name: "", delivery_route: "", expiry_date: "" });
      toast.success("Invitation issued.");
    } catch { toast.error("Could not issue invitation."); }
    setBusy(false);
  };
  return (
    <section className="rounded-2xl border border-stone-200 bg-white p-6" data-testid="staff-invite-form">
      <div className="flex items-center gap-2"><Send className="h-5 w-5 text-mill-moss" /><h3 className="font-display text-lg font-semibold text-mill-ink">Invite a volunteer or group leader</h3></div>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-xs text-mill-muted">Role pathway</Label>
          <select data-testid="invite-role" className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
            {config?.roles.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
          {config && <p className="mt-1 text-xs text-mill-muted">Pack: {config.onboarding_packs[f.role].name} {config.onboarding_packs[f.role].version}</p>}
        </div>
        <div>
          <Label className="text-xs text-mill-muted">Activation rule</Label>
          <select data-testid="invite-activation" className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm" value={f.activation_rule} onChange={(e) => setF({ ...f, activation_rule: e.target.value })}>
            <option value="approval">Administrator approval</option>
            <option value="automatic">Automatic on completion</option>
          </select>
        </div>
        <div><Label className="text-xs text-mill-muted">Display name</Label><Input value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })} data-testid="invite-name" /></div>
        <div><Label className="text-xs text-mill-muted">Team / group (optional)</Label><Input value={f.team} onChange={(e) => setF({ ...f, team: e.target.value })} data-testid="invite-team" /></div>
        <div><Label className="text-xs text-mill-muted">Email / delivery route</Label><Input value={f.delivery_route} onChange={(e) => setF({ ...f, delivery_route: e.target.value })} data-testid="invite-route" /></div>
        <div><Label className="text-xs text-mill-muted">Expiry date</Label><Input type="date" value={f.expiry_date} onChange={(e) => setF({ ...f, expiry_date: e.target.value })} data-testid="invite-expiry" /></div>
      </div>
      <div className="mt-3">
        <Label className="text-xs text-mill-muted">Offer these optional sharing choices</Label>
        <div className="mt-1.5 space-y-1">
          {(config?.consent_choices || []).filter((c) => c.category === "optional").map((c) => (
            <label key={c.id} className="flex items-center gap-2 text-sm text-mill-ink">
              <Checkbox checked={f.offered_optional_consents.includes(c.id)} onCheckedChange={() => toggleOpt(c.id)} data-testid={`invite-opt-${c.id}`} /> {c.label}
            </label>
          ))}
        </div>
      </div>
      <Button className="mt-4 bg-mill-moss hover:bg-mill-moss-dark" onClick={submit} disabled={busy} data-testid="invite-submit">Issue invitation</Button>
      {lastUrl && (
        <div className="mt-4 flex items-center gap-2 rounded-xl bg-mill-alt/60 p-3" data-testid="invite-created-url">
          <p className="flex-1 truncate font-mono text-xs text-mill-ink">{lastUrl}</p>
          <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(lastUrl); toast.success("Link copied."); }} data-testid="invite-copy-created"><Copy className="mr-1 h-3.5 w-3.5" /> Copy link</Button>
        </div>
      )}
    </section>
  );
}

function InviteRow({ inv, isAdmin, onChange }) {
  const [showShared, setShowShared] = useState(false);
  const act = async (action, body = {}) => {
    try { await post(`/core/invites/${inv.id}/${action}`, body); await onChange(); toast.success(`Done: ${action.replace(/-/g, " ")}.`); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || `Could not ${action}.`); }
  };
  const p = inv.progress;
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid={`staff-invite-${inv.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-semibold text-mill-ink">{inv.display_name} <span className="text-sm font-normal text-mill-muted">· {inv.role}{inv.team ? ` · ${inv.team}` : ""}</span></p>
          <p className="font-mono text-xs text-mill-muted">{inv.delivery_route} · expires {inv.expiry_date}</p>
        </div>
        <StatePill state={inv.lifecycle} />
      </div>
      {p && (
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <Chip on={p.profile_complete}>Details</Chip>
          <Chip on={p.induction_complete}>Induction</Chip>
          <Chip on={p.role_active}>Role active</Chip>
        </div>
      )}
      {inv.clarification_note && <p className="mt-2 rounded-lg bg-[#FEF3C7] px-3 py-2 text-xs text-[#B45309]" data-testid={`clarify-note-${inv.id}`}>Returned for clarification: {inv.clarification_note}</p>}
      {p?.shared_info && Object.keys(p.shared_info.fields).length > 0 && (
        <div className="mt-3">
          <button onClick={() => setShowShared((s) => !s)} className="inline-flex items-center gap-1.5 text-xs font-medium text-katlas-blue hover:underline" data-testid={`shared-toggle-${inv.id}`}>
            {showShared ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />} {showShared ? "Hide" : "Review"} operationally shared information
          </button>
          {showShared && (
            <div className="mt-2 rounded-xl bg-mill-alt/50 p-3 text-xs" data-testid={`shared-info-${inv.id}`}>
              <dl className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {Object.entries(p.shared_info.fields).map(([k, v]) => (
                  <div key={k}><dt className="text-[10px] uppercase tracking-wider text-mill-muted">{k}</dt><dd className="text-mill-ink">{v}</dd></div>
                ))}
              </dl>
              <p className="mt-2 text-[11px] text-mill-muted">Optional private responses are not shown — only what {inv.display_name} chose to share for role administration.</p>
            </div>
          )}
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(inv.invite_url); toast.success("Link copied."); }} data-testid={`copy-${inv.id}`}><Copy className="mr-1 h-3.5 w-3.5" /> Copy link</Button>
        <Link to={`/invite/${inv.token}`} data-testid={`open-invitee-${inv.id}`} className="inline-flex items-center gap-1.5 rounded-md bg-katlas-blue px-3 py-1.5 text-xs font-semibold text-white hover:bg-katlas-blue-dark">Open as invitee <ExternalLink className="h-3.5 w-3.5" /></Link>
        {isAdmin && inv.lifecycle === "awaiting_review" && <Button size="sm" className="bg-mill-moss hover:bg-mill-moss-dark" onClick={() => act("activate")} data-testid={`activate-${inv.id}`}><ShieldCheck className="mr-1 h-3.5 w-3.5" /> Activate</Button>}
        {isAdmin && inv.lifecycle === "awaiting_review" && <Button size="sm" variant="outline" onClick={() => { const n = prompt("What clarification is needed?"); if (n !== null) act("clarify", { note: n }); }} data-testid={`clarify-${inv.id}`}><Undo2 className="mr-1 h-3.5 w-3.5" /> Return for clarification</Button>}
        {isAdmin && !["role_active", "revoked", "expired", "suspended"].includes(inv.lifecycle) && !inv.needs_support && <Button size="sm" variant="outline" onClick={() => act("support", { note: "Assisted onboarding offered." })} data-testid={`support-${inv.id}`}><LifeBuoy className="mr-1 h-3.5 w-3.5" /> Offer support</Button>}
        {isAdmin && inv.needs_support && <Button size="sm" variant="outline" onClick={() => act("clear-support")} data-testid={`clear-support-${inv.id}`}><Check className="mr-1 h-3.5 w-3.5" /> Clear support flag</Button>}
        {isAdmin && inv.lifecycle === "role_active" && <Button size="sm" variant="outline" onClick={() => act("suspend")} data-testid={`suspend-${inv.id}`}><PauseCircle className="mr-1 h-3.5 w-3.5" /> Suspend</Button>}
        {isAdmin && inv.lifecycle === "suspended" && <Button size="sm" variant="outline" onClick={() => act("reinstate")} data-testid={`reinstate-${inv.id}`}><PlayCircle className="mr-1 h-3.5 w-3.5" /> Reinstate</Button>}
        {isAdmin && <Button size="sm" variant="outline" onClick={() => act("revoke")} disabled={inv.status === "revoked"} data-testid={`revoke-${inv.id}`}><Ban className="mr-1 h-3.5 w-3.5" /> Revoke</Button>}
      </div>
    </div>
  );
}

function Chip({ on, children }) {
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${on ? "bg-[#D1FAE5] text-[#065F46]" : "bg-stone-100 text-stone-500"}`}>{on ? <Check className="h-3 w-3" /> : <span className="h-1.5 w-1.5 rounded-full bg-stone-400" />}{children}</span>;
}
